import React, { useMemo } from 'react';
import { Box, useMediaQuery, useTheme } from '@mui/material';

const SIZES = {
  xs: { R: 32, COLS: 4, ROWS: 4 },
  sm: { R: 44, COLS: 5, ROWS: 5 },
  md: { R: 54, COLS: 6, ROWS: 6 },
  lg: { R: 64, COLS: 7, ROWS: 7 },
};

function hexPoints(cx, cy, R) {
  const pts = [];
  for (let i = 0; i < 6; i++) {
    const a = (Math.PI / 180) * (60 * i); // flat-top
    pts.push(`${(cx + R * Math.cos(a)).toFixed(2)},${(cy + R * Math.sin(a)).toFixed(2)}`);
  }
  return pts.join(' ');
}

function mulberry32(seed) {
  let s = seed >>> 0;
  return function () {
    s = (s + 0x6D2B79F5) >>> 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export default function HexagonDecoration({ position = 'top-left', color }) {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.only('xs'));
  const isSm = useMediaQuery(theme.breakpoints.only('sm'));
  const isMd = useMediaQuery(theme.breakpoints.only('md'));

  const { R, COLS, ROWS } = isXs ? SIZES.xs : isSm ? SIZES.sm : isMd ? SIZES.md : SIZES.lg;

  const COLOR = color ?? theme.palette.background.paper;

  const COL_W = R * 1.5;
  const HEX_H = R * Math.sqrt(3);

  const isBottomRight = position === 'bottom-right';
  const uid = isBottomRight ? 'br' : 'tl';

  const svgW = COLS * COL_W + R + R;
  const svgH = ROWS * HEX_H + HEX_H / 2 + HEX_H / 2;

  const hexagons = useMemo(() => {
    const items = [];
    for (let row = 0; row < ROWS; row++) {
      for (let col = 0; col < COLS; col++) {
        const x = col * COL_W + R;
        const y = row * HEX_H + (col % 2 ? HEX_H / 2 : 0) + HEX_H / 2;

        const rng = mulberry32(row * 97 + col * 31 + 42);
        const v1 = rng(), v2 = rng(), v3 = rng(), v4 = rng();

        const normDist =
          Math.sqrt((col / Math.max(COLS - 1, 1)) ** 2 + (row / Math.max(ROWS - 1, 1)) ** 2) / Math.SQRT2;
        const prominence = Math.pow(Math.max(0, 1 - normDist), 1.6);

        if (prominence < 0.02) continue;

        const variant =
          v1 < 0.38 ? 'outline' :
          v1 < 0.68 ? 'filled-low' : 'filled-mid';

        const animDuration = 2.5 + v2 * 3.5;
        const animDelay = v3 * 5;
        const doAnimate = v4 < 0.18 && prominence > 0.25;

        items.push({ x, y, variant, prominence, animDuration, animDelay, doAnimate, id: `h-${uid}-${row}-${col}` });
      }
    }
    return items;
  }, [uid, R, COLS, ROWS, COL_W, HEX_H]);

  const posStyle = isBottomRight
    ? { bottom: '-20px', right: '-20px' }
    : { top: '-20px', left: '-20px' };

  return (
    <Box
      sx={{
        position: 'absolute',
        ...posStyle,
        zIndex: 2,
        pointerEvents: 'none',
        overflow: 'visible',
      }}
    >
      <svg
        width={svgW}
        height={svgH}
        style={{
          transform: isBottomRight ? 'rotate(180deg)' : 'none',
          overflow: 'visible',
          display: 'block',
        }}
      >
        <defs>
          <style>{`
            @keyframes hexPulse_${uid} {
              0%, 100% { opacity: 1; }
              50%       { opacity: 0.15; }
            }
          `}</style>

          <radialGradient
            id={`fadeMask_${uid}`}
            cx="0%" cy="0%" r="141%"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0%"   stopColor="white" stopOpacity="1" />
            <stop offset="45%"  stopColor="white" stopOpacity="0.75" />
            <stop offset="75%"  stopColor="white" stopOpacity="0.3" />
            <stop offset="100%" stopColor="white" stopOpacity="0" />
          </radialGradient>
          <mask id={`mask_${uid}`}>
            <rect x="0" y="0" width={svgW} height={svgH} fill={`url(#fadeMask_${uid})`} />
          </mask>
        </defs>

        <g mask={`url(#mask_${uid})`}>
          {hexagons.map(({ x, y, variant, prominence, animDuration, animDelay, doAnimate, id }) => {
            const pts = hexPoints(x, y, R);
            const strokeOp = Math.min(0.9, 0.15 + prominence * 0.75);
            const strokeW = 0.8 + prominence * 1.2;
            const animStyle = doAnimate
              ? { animation: `hexPulse_${uid} ${animDuration}s ease-in-out ${animDelay}s infinite` }
              : {};

            const base = { points: pts, stroke: COLOR, strokeWidth: strokeW, strokeOpacity: strokeOp };

            switch (variant) {
              case 'outline':
                return <polygon key={id} {...base} fill="none" style={animStyle} />;

              case 'filled-low':
                return (
                  <polygon key={id} {...base}
                    fill={COLOR}
                    fillOpacity={0.04 + prominence * 0.1}
                    style={animStyle}
                  />
                );

              case 'filled-mid':
                return (
                  <polygon key={id} {...base}
                    fill={COLOR}
                    fillOpacity={0.1 + prominence * 0.22}
                    strokeWidth={strokeW * 1.2}
                    style={animStyle}
                  />
                );

              default:
                return null;
            }
          })}
        </g>
      </svg>
    </Box>
  );
}
